'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Sword, Wind, Shield, Scale } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function DeuxEpeesPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-gray-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Épées</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Deux</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/epees"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Épées'e Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-gray-900/30 to-slate-900/30 rounded-xl p-8 mb-8 border border-gray-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-gray-100 to-slate-50 border border-gray-300 flex flex-col items-center justify-center relative">
                {/* Card Visual Design */}
                <div className="text-center">
                  <div className="text-6xl mb-4">⚔️</div>
                  <div className="w-16 h-1 bg-gray-600 mx-auto mb-4"></div>
                  <div className="text-2xl font-bold text-gray-800">DEUX</div>
                  <div className="text-sm text-gray-700 mt-2">d'Épées</div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 text-gray-600">
                  <Sword className="h-4 w-4" />
                </div>
                <div className="absolute top-4 right-4 text-gray-600">
                  <Sword className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 left-4 text-gray-600">
                  <Scale className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 right-4 text-gray-600">
                  <Scale className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-slate-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Deux d'Épées</h1>
                  <p className="text-xl text-gray-400">Karar Verme / Denge Arayışı</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                İki kılıcın kesiştiği nokta, zorlu kararların ve ikilemde kalmanın simgesi.
                Zihinsel denge arayışı ve objektif değerlendirme ihtiyacını temsil eder.
                Çatışan fikirler arasında adil çözüm bulma çabası.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-gray-600/20 text-gray-300 rounded-full text-sm">Karar Verme</span>
                <span className="px-3 py-1 bg-gray-600/20 text-gray-300 rounded-full text-sm">Denge</span>
                <span className="px-3 py-1 bg-gray-600/20 text-gray-300 rounded-full text-sm">İkilem</span>
                <span className="px-3 py-1 bg-gray-600/20 text-gray-300 rounded-full text-sm">Objektiflik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Scale className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dengeli Karar</h3>
                  <p className="text-slate-400 text-sm">Tüm seçenekleri değerlendirerek adil karar verme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Barış ve Uyum</h3>
                  <p className="text-slate-400 text-sm">Çatışan taraflar arasında uzlaşma sağlama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sabırlı Düşünme</h3>
                  <p className="text-slate-400 text-sm">Aceleci davranmayıp dikkatli analiz yapma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Diplomasi</h3>
                  <p className="text-slate-400 text-sm">Zor durumları diplomatik yollarla çözme.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sword className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararsızlık</h3>
                  <p className="text-slate-400 text-sm">Karar verememek, sürekli iki arada bir derede kalmak.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Çatışma</h3>
                  <p className="text-slate-400 text-sm">Uzlaşmazlık, farklı görüşler arasında gerginlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Önyargı</h3>
                  <p className="text-slate-400 text-sm">Objektif olmayan, taraflı yaklaşım.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Blokaj</h3>
                  <p className="text-slate-400 text-sm">İlerleme kaydedememek, çıkmaza girme.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sword className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İki Kılıç</h3>
              <p className="text-slate-400 text-sm">Çatışan düşünceler ve karar verme süreci.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Denge</h3>
              <p className="text-slate-400 text-sm">Adalet ve objektif değerlendirme.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Wind className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Hava Elementi</h3>
              <p className="text-slate-400 text-sm">Zihinsel süreçler ve düşünme gücü.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İş kararları</li>
                <li>• Çatışma çözümü</li>
                <li>• Partnerlik değerlendirmeleri</li>
                <li>• Diplomatik görüşmeler</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Scale className="h-5 w-5 text-gray-400" />
                İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkide denge kurma</li>
                <li>• Anlaşmazlık çözümü</li>
                <li>• Karşılıklı anlayış</li>
                <li>• Barışçıl çözümler</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/epees/as"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: As d'Épées
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/epees/trois"
            className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Trois d'Épées
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}